import { REPEATABLE_PAGES, SITE_PAGES, pagePath } from "../pages/registry";
import type { Origin } from "../settings/types";
/**
 * THE SCREEN REGISTRY.
 *
 * One entry per configurable screen. It feeds the organiser bar's screen picker,
 * and it is deliberately the ONLY list: a screen that exists but is missing from
 * here becomes unreachable from the bar, and one listed but not routed leads
 * nowhere. Router and picker read the same source.
 *
 * ⚠️ The picker must reach screens REMOVED FROM NAVIGATION too. Otherwise
 * unpublishing "Mon badge" would make it impossible to configure again — you
 * would have to republish it to reach its settings, in front of everyone.
 *
 * Phase 2 fills this in, screen by screen.
 */
export type Screen = {
  /** Router path. */
  path: string;
  /** Catalog scope — `CATALOG[key]` holds this screen's own settings. */
  key: string;
  /** Organiser-facing name. French: this is a French product. */
  label: string;
  /**
   * Reachable WITHOUT a session. The exception, not the rule: a participant's
   * screens are their own, and the sign-in screen is the one place you can be
   * before you have proved who you are.
   */
  open?: boolean;
  /**
   * SERVI SANS LE MOBILIER DU SITE : ni barre, ni menu, ni pied de page, ni
   * sélecteur de langue, ni bouton de configuration.
   *
   * ⚠️ RÉSERVÉ AUX SURFACES QUI NE SONT PAS DES PAGES. L'écran de salle est une
   * PROJECTION : personne ne navigue dessus, personne ne change sa langue, et
   * tout ce qui n'est pas le contenu vole de la place à ce qui doit se lire à
   * vingt mètres.
   */
  bare?: boolean;
  /**
   * SERVI SANS L'IMAGE D'EN-TÊTE, mais avec tout le reste du châssis.
   *
   * ⚠️ LE CRAN D'AVANT `bare`, PAS UNE VARIANTE. Une console qu'on pilote reste une
   * page : on y navigue, on s'y déconnecte, on y ouvre le panneau. Mais les 180
   * pixels de communication de l'événement y poussent les commandes sous la ligne de
   * flottaison, sur l'écran où chaque seconde compte (#347).
   */
  noBanner?: boolean;
  /**
   * Part of the REGISTRATION FUNNEL, and under which name it appears in the
   * breadcrumb. The order is this list's order — a second list of steps would
   * drift from the routes, and the symptom would be a breadcrumb pointing at a
   * step that no longer exists.
   *
   * The value is the SETTING that names the step — an organiser renames it like
   * anything else, and the name it falls back to is declared ONCE, in the
   * catalog, where the panel already shows it as the field's placeholder.
   */
  funnel?: string;
  /**
   * The step's default name — what the site calls it until the organiser renames
   * it. Declared HERE, beside the setting that overrides it, because the catalog
   * builds its row from this entry: two sources for one word drift apart, and the
   * panel then promises a default the breadcrumb does not use.
   */
  stepName?: string;
  /**
   * Can this step be taken out of the funnel?
   *
   * ⚠️ THE CONFIRMATION CANNOT. It is where the answers are WRITTEN: removing it
   * would leave a funnel that asks everything and records nothing, and the fault
   * would only surface at the door. The prototype fixes it for the same reason.
   */
  removable?: boolean;
  /**
   * HORS DU PARCOURS TANT QUE PERSONNE NE L'A AJOUTÉE — l'inverse du réglage par
   * défaut des autres étapes.
   *
   * ⚠️ CE N'EST PAS UNE PRÉFÉRENCE, C'EST CE QUE FAIT LA V1. Les étapes de base
   * (participation, typologie, informations) sont servies tant qu'on ne les retire
   * pas ; la société, elle, n'existe que sur les événements qui la demandent —
   * `FO_MEETING_COMPANIES_SHOW` est absent chez presque tous, et son absence vaut
   * « non ». Servie par défaut, la V2 aurait réclamé un SIRET à des visiteurs de
   * salons qui n'en ont jamais entendu parler.
   *
   * La conséquence traverse deux fichiers : le catalogue n'allume pas son
   * interrupteur par défaut (`onByDefault`), et `funnel/thread.ts` lit l'état
   * EFFECTIF de cet interrupteur au lieu de ne retirer que sur un `false` explicite.
   */
  optIn?: boolean;
  /**
   * ═══ CETTE ÉTAPE PEUT ÊTRE SERVIE PLUSIEURS FOIS ═══
   *
   * Un événement demande parfois deux formulaires — l'un pour l'identité, l'autre pour
   * des questions propres à l'édition — et rien dans le produit ne s'y oppose : les
   * réponses sont rangées PAR MODÈLE (`{ U, UE, C }`), pas par formulaire, donc deux
   * formulaires servant des champs différents ne se marchent pas dessus.
   *
   * ⚠️ LA RÉPÉTABILITÉ EST UNE PROPRIÉTÉ DE L'ÉTAPE, DONC ELLE EST DÉCLARÉE ICI. Écrite
   * dans `funnel/order.ts`, elle aurait fait dépendre le registre de l'ordre — alors que
   * c'est l'ordre qui lit le registre.
   */
  repeatable?: true;
  /**
   * ═══ CETTE ÉTAPE PEUT AUSSI ÊTRE SERVIE COMME UNE PAGE DU SITE ═══
   *
   * Le menu et l'accueil peuvent y mener directement : le participant revient sur ses
   * informations sans retraverser l'inscription. La page sert le MÊME écran, sous le
   * même nom, avec les mêmes réglages — pas une copie.
   *
   * ⚠️ SEULES LES ÉTAPES QUI ENREGISTRENT SEULES PEUVENT L'ÊTRE, et ce n'est pas une
   * restriction de prudence : servie en page, une étape qui ne pose ses réponses qu'à la
   * confirmation aurait un bouton qui n'écrit rien. Les conférences en sont le dernier
   * exemple : elles écrivent bien au clic, mais leur place se prend et se rend en
   * direct, donc il n'y a rien à « enregistrer » ensuite. Les quatre autres
   * (participation, typologie, dates, billetterie) ont rejoint la liste le 2026-08-09,
   * quand `funnel/stepSave.tsx` leur a donné une écriture.
   *
   * ⚠️ ET UN NOM À ELLE TANT QUE PERSONNE N'EN A ÉCRIT UN. Le site porte déjà une page
   * « Informations » (accès, horaires, wifi) : servie sous le nom de l'étape, la nôtre
   * aurait fait DEUX entrées « Informations » dans le même menu. Au possessif, elle dit
   * en plus la bonne chose — ce sont VOS réponses. Renommée dans le parcours, elle suit
   * ce nom-là : c'est une seule chose, elle n'a qu'un mot.
   *
   * ⚠️ ET PARFOIS UNE ADRESSE À ELLE. Celle d'une page se déduit de la clé de l'étape
   * (`/dates`, `/company`), sauf quand cette clé est DÉJÀ une adresse : l'étape de
   * participation s'appelle `registration`, et sa page aurait pris `/registration`,
   * c'est-à-dire la première étape du tunnel. Deux routes de même adresse, et c'est la
   * déclarée en premier qui gagne : la page n'aurait jamais été atteinte, en silence.
   */
  standalone?: {
    name: string;
    icon: string;
    subtitle: string;
    /** Son adresse, quand la clé de l'étape est déjà prise. */
    slug?: string;
  };
  /**
   * D'OÙ VIENT L'INTERRUPTEUR DE CETTE ÉTAPE DANS LA V1.
   *
   * Déclarée ICI et pas dans le catalogue, parce que la ligne de l'étape y est
   * ENGENDRÉE depuis ce registre (`stepShowKey`) : c'est le seul endroit où l'étape
   * existe une fois. Voir `Origin` — la correspondance est déclarée, jamais devinée.
   */
  origin?: Origin;
};

export const SCREENS: ReadonlyArray<Screen> = [
  { path: "/", key: "home", label: "Accueil" },
  { path: "/login", key: "login", label: "Connexion", open: true },
  {
    path: "/registration",
    key: "registration",
    label: "Inscription · étape 1",
    funnel: "FO_MEETING_V2_REGISTRATION_STEP_LABEL",
    stepName: "Participation",
    removable: true,
    standalone: {
      name: "Ma participation",
      /* Le repère de lieu, celui de la réponse « sur place ». C'est de cette réponse-là
         que la page parle, et le dessin de l'inscription est déjà celui de l'entrée du
         tunnel, dans le même menu. */
      icon: "onsite",
      subtitle: "Votre réponse à l'invitation",
      /* `/registration` est déjà la première étape du tunnel. Voir `standalone`. */
      slug: "participation",
    },
  },
  {
    path: "/registration/typology",
    key: "typology",
    label: "Inscription · typologie",
    funnel: "FO_MEETING_V2_TYPO_STEP_LABEL",
    stepName: "Typologie",
    removable: true,
    origin: {
      key: "FO_MEETING_TYPO_IS_ACTIVE",
      note: "Vérifié : `screens/steps/Steps.jsx` de la V1 en fait une étape du tunnel. À ne pas confondre avec `FO_MEETING_CHOOSE_TYPO`, qui est une redirection de route (`privateRoute.tsx`).",
    },
    standalone: {
      name: "Mon profil",
      /* Pas le dessin de profil : il nomme déjà « Mes informations », et deux tuiles
         voisines portant le même se lisent comme un doublon. */
      icon: "etoile",
      subtitle: "Le profil sous lequel vous venez",
    },
  },
  /**
   * ⚠️ AVANT LES INFORMATIONS, COMME DANS LA V1 : son `Steps.jsx` place les dates
   * juste après la typologie, et le formulaire ensuite. Ce n'est pas indifférent —
   * le profil peut décider des jours ouverts, et le formulaire est la partie longue :
   * on ne fait pas remplir vingt champs à quelqu'un pour lui apprendre après coup
   * qu'aucune date ne lui convient.
   */
  {
    path: "/registration/dates",
    key: "dates",
    label: "Inscription · dates",
    funnel: "FO_MEETING_V2_DATES_STEP_LABEL",
    stepName: "Dates",
    removable: true,
    origin: {
      key: "FO_MEETING_DATES_BY_PASS",
      // La V1 nomme ce qu'elle SAUTE, la V2 ce qu'elle SERT.
      invert: true,
      note: "Vérifié : `hooks/useConf.js` de la V1 en fait la moitié de `datesHide` (`event?.days.length < 2 || FO_MEETING_DATES_BY_PASS`), que `screens/steps/Dates.jsx` lit pour se sauter lui-même. L'autre moitié, soit moins de deux jours, n'est pas un réglage : c'est une propriété de l'événement, et `funnel/thread.ts` la calcule.",
    },
    standalone: {
      name: "Mes journées",
      icon: "dates",
      subtitle: "Les jours où vous venez",
    },
  },
  /**
   * ⚠️ ENTRE LES DATES ET LES INFORMATIONS, COMME DANS LA V1, et l'ordre porte une
   * règle : les articles se vendent PAR JOUR, et on ne peut proposer le déjeuner du
   * mardi qu'à qui a déjà dit venir le mardi. Placée avant les dates, l'étape n'aurait
   * eu que le niveau « hors jour » à offrir — voir `etalage` dans `funnel/articles.ts`.
   * Placée après le formulaire, elle ferait remplir vingt champs avant d'annoncer le
   * prix, ce que la V1 évite déjà pour la même raison qu'elle met les dates devant.
   */
  {
    path: "/registration/articles",
    key: "articles",
    label: "Inscription · billetterie",
    funnel: "FO_MEETING_V2_ARTICLES_STEP_LABEL",
    stepName: "Billetterie",
    removable: true,
    // Comme la société et les conférences : absente tant que l'organisateur ne la
    // demande pas. La plupart des événements ne vendent rien.
    optIn: true,
    origin: {
      key: "FO_MEETING_ARTICLES_IS_ACTIVE",
      note: "Vérifié : `hooks/useConf.js` de la V1 l'expose en `articlesActive`, et `screens/steps/Steps.jsx` n'ajoute l'étape « Articles » que si elle est vraie. À ne pas confondre avec `FO_MEETING_ARTICLES_AUTO`, qui garde l'étape mais la SAUTE en reprenant la sélection déjà enregistrée (`screens/steps/Articles.jsx`). La V2 ne la reprend pas : retirer l'étape dit la même chose sans un second interrupteur.",
    },
    standalone: {
      name: "Ma commande",
      /* Le document, comme la facture qu'elle finit par produire. Le dessin du badge
         est déjà pris par la page du badge. */
      icon: "fichier",
      subtitle: "Ce que vous avez retenu",
    },
  },
  {
    path: "/registration/informations",
    key: "informations",
    label: "Inscription · informations",
    funnel: "FO_MEETING_V2_INFO_STEP_LABEL",
    stepName: "Informations",
    removable: true,
    repeatable: true,
    origin: {
      key: "FO_MEETING_INFO_IS_ACTIVE",
      note: "Vérifié : `screens/steps/Steps.jsx` de la V1.",
    },
    standalone: {
      name: "Mes informations",
      icon: "profil",
      subtitle: "Vos réponses au formulaire",
    },
  },
  /**
   * ⚠️ APRÈS LES INFORMATIONS, COMME DANS LA V1. Son `Steps.jsx` insère « Sociétés »
   * une fois le formulaire du participant passé : on demande d'abord qui vous êtes,
   * ensuite pour qui vous venez. L'ordre de cette liste EST celui du fil d'Ariane.
   */
  {
    path: "/registration/company",
    key: "company",
    label: "Inscription · société",
    funnel: "FO_MEETING_V2_COMPANY_STEP_LABEL",
    stepName: "Société",
    removable: true,
    // La seule étape que l'organisateur AJOUTE au lieu de la retirer — voir `optIn`.
    optIn: true,
    origin: {
      key: "FO_MEETING_COMPANIES_SHOW",
      note: "Vérifié : `hooks/useConf.js` de la V1 l'expose en `companiesShow`, et `screens/steps/Steps.jsx` n'ajoute l'étape « Sociétés » que si elle est vraie. À ne pas confondre avec `FO_MEETING_COMPANIES_FORM_ID`, que plus aucun écran de la V1 ne lit.",
    },
    standalone: {
      name: "Mes sociétés",
      icon: "societe",
      subtitle: "La société que vous représentez",
    },
  },
  /**
   * ⚠️ APRÈS LA SOCIÉTÉ, ET CE N'EST PAS QU'UNE HABITUDE DE LA V1 : la route qui rend
   * les conférences FILTRE sur les catégories de la société du participant. Placée
   * avant, elle proposerait à quelqu'un qui n'est encore rattaché à rien un choix
   * amputé de tout ce qui est catégorisé.
   */
  {
    path: "/registration/conferences",
    key: "conferences",
    label: "Inscription · conférences",
    funnel: "FO_MEETING_V2_CONFERENCES_STEP_LABEL",
    stepName: "Conférences",
    removable: true,
    // Comme la société : absente tant que l'organisateur ne la demande pas.
    optIn: true,
    origin: {
      key: "FO_MEETING_CONFERENCES_V2_IS_ACTIVE",
      note: "Vérifié : `hooks/useConf.js` de la V1 l'expose en `showConferencesV2`, et `screens/steps/Steps.jsx` n'ajoute l'étape que si elle est vraie. À NE PAS confondre avec `FO_MEETING_CONFERENCES_IS_ACTIVE`, qui allume l'ANCIEN écran de conférences (`screens/Conferences/`) : deux systèmes distincts, et c'est le second que la V2 reprend (voir `funnel/conferences.ts`).",
    },
  },
  /**
   * ⚠️ APRÈS TOUT LE RESTE, ET JUSTE AVANT LA CONFIRMATION. On n'inscrit quelqu'un
   * d'autre qu'une fois sa propre inscription remplie : l'accompagnant hérite de la
   * typologie du porteur et, quand on ne lui demande pas ses jours, des siens. Placée
   * plus tôt, l'étape aurait recopié des réponses qui n'existaient pas encore.
   */
  {
    path: "/registration/companions",
    key: "companions",
    label: "Inscription · accompagnants",
    funnel: "FO_MEETING_V2_COMPANIONS_STEP_LABEL",
    stepName: "Accompagnants",
    removable: true,
    // Comme la société, les conférences et la billetterie : absente tant que
    // l'organisateur ne la demande pas.
    optIn: true,
    origin: {
      key: "FO_MEETING_COMPANIONS_IS_ACTIVE",
      note: "Vérifié : l'événement de test la porte, et `hooks/useConf.js` de la V1 l'expose pour décider si le module d'accompagnants existe.",
    },
    standalone: {
      name: "Mes accompagnants",
      /* Deux silhouettes : « moi et les miens ». `contact` est un TÉLÉPHONE — il sert
         au « Une question ? » du pied de page. */
      icon: "partenaires",
      subtitle: "Les personnes qui vous accompagnent",
    },
  },
  {
    path: "/registration/confirmation",
    key: "confirmation",
    label: "Inscription · confirmation",
    funnel: "FO_MEETING_V2_CONFIRMATION_STEP_LABEL",
    stepName: "Confirmation",
  },
];

/** The funnel's steps, in order. */
export const FUNNEL = SCREENS.filter(
  (s): s is Screen & { funnel: string; stepName: string } => !!s.funnel,
);

/**
 * ═══ THE SITE'S PAGES ARE CONFIGURABLE SCREENS TOO ═══
 *
 * ⚠️ AND THE PROMISE AT THE TOP OF THIS FILE DEMANDS IT, with "Mon badge" as its
 * example: the screen picker must reach the screens REMOVED FROM NAVIGATION. Without
 * this list, unpublishing the badge page made it impossible to configure again — it
 * would have to be republished, in front of the participants, to reach its settings.
 *
 * ⚠️ GENERATED FROM `SITE_PAGES`, never copied: their key names the route, the catalog
 * scope (`CATALOG.badge`) and the page manager's three settings all at once. A second
 * hand-written list would have drifted from the first, and the symptom would be a
 * screen quietly losing its own settings.
 *
 * Their `label` is the DEFAULT name. Where the organiser may have renamed the page, it
 * is their name that is displayed (see `useSitePages` and the organiser bar); the
 * cascade is not available here.
 */
export const PAGE_SCREENS: ReadonlyArray<Screen> = SITE_PAGES.map((page) => ({
  path: pagePath(page),
  key: page.key,
  label: page.name,
}));

/** Everything that can be configured, in the order the picker presents it. */
/**
 * ═══ LES DEUX OUTILS DU DIRECT ═══
 *
 * Déclarés ici et pas dans `SCREENS` parce qu'ils ne sont ni des étapes ni des pages
 * du site : ils ne s'allument pas, ne s'ordonnent pas, et n'apparaissent dans aucun
 * menu de participant. Ce qu'ils viennent chercher dans le registre, c'est la GARDE
 * DE SESSION et le sélecteur d'écran de la barre organisateur.
 *
 * ⚠️ L'ÉCRAN DE SALLE EST `open`, ET C'EST LA RAISON POUR LAQUELLE IL EXISTE ICI.
 * Il s'authentifie par un JETON D'ÉCRAN, pas par une session : personne ne se
 * connecte sur la machine au fond d'une salle. Sans cette ligne, `needsSession`
 * répond « oui » pour toute adresse qu'il ne connaît pas, et le châssis renvoyait
 * le vidéoprojecteur sur le formulaire de connexion. Constaté en le regardant
 * tourner, alors que les tests unitaires et l'API étaient verts : la garde vit dans
 * le châssis, pas dans l'écran, donc aucun test de l'écran ne pouvait la voir.
 *
 * ⚠️ LA RÉGIE, ELLE, EXIGE BIEN UNE SESSION, et le serveur exige en plus un rôle
 * sur le domaine. Les deux contrôles ne font pas double emploi : celui-ci évite
 * d'afficher une console à qui n'a rien à y faire, celui du serveur est le seul qui
 * compte.
 */
export const LIVE_TOOLS: ReadonlyArray<Screen> = [
  { path: "/live/regie", key: "regie", label: "Régie du direct", noBanner: true },
  /**
   * ⚠️ SON PROPRE ÉCRAN, PARCE QU'IL Y EN A BEAUCOUP. La file de modération vivait
   * en bas de la régie : une plénière en empile des centaines, et la liste poussait
   * le pilotage hors de l'écran. Séparée, elle se tient sur un second appareil —
   * celui qui modère n'est souvent pas celui qui pilote.
   */
  { path: "/live/questions", key: "moderation", label: "Questions du public" },
  {
    path: "/live/scene", key: "scene", label: "Écran de salle", open: true, bare: true,
  },
];

/**
 * CET ÉCRAN SE SERT SANS LE CHÂSSIS DU SITE.
 *
 * ⚠️ VU EN LE PROJETANT, PAS EN LE LISANT. L'écran de salle portait le menu du
 * site, le sélecteur de langue, le pied de page et le bouton « Configurer le
 * site » : du mobilier de participant sur un mur de dix mètres, au milieu d'une
 * plénière. Rien dans le code ne le signalait, parce que le châssis est posé par
 * `Shell` et qu'aucun test de l'écran ne le traverse.
 */
export function isBare(pathname: string): boolean {
  return screenOf(pathname)?.bare === true;
}

/**
 * ═══ L'IMAGE D'EN-TÊTE, PARTOUT SAUF OÙ ELLE GÊNE ═══
 *
 * Retirée de l'écran nu (voir `isBare`) et de la régie (voir `noBanner`) : la
 * première n'est pas une page, la seconde est une console où l'image repoussait le
 * pilotage hors de l'écran.
 *
 * ⚠️ UNE ADRESSE INCONNUE LA GARDE, et c'est l'inverse du pied de page : l'écran
 * « Adresse inconnue » est une page du site comme une autre, il porte sa barre et
 * son bandeau. Le défaut ne retire rien.
 *
 * ⚠️ ÉCRITE ICI ET PAS DANS `Shell`, pour la même raison que `hasFooter` : le
 * châssis n'a pas de test qui le traverse.
 */
export function hasBanner(pathname: string): boolean {
  const screen = screenOf(pathname);
  return screen?.bare !== true && screen?.noBanner !== true;
}

/**
 * ═══ LES COORDONNÉES DE L'ÉVÉNEMENT N'APPARTIENNENT QU'À L'ACCUEIL ═══
 *
 * Organisateur, dates, lieu, contact : sur la home et nulle part ailleurs. Ailleurs, la
 * page s'arrête à son contenu — puis à l'image et aux mentions, qui ferment TOUS les
 * écrans et sont montées HORS de cette règle (`FooterEnd` dans `Shell`). L'ourlet de la
 * page est dû partout ; la fiche d'identité de l'événement, non.
 *
 * ⚠️ L'ACCUEIL, CONNECTÉ COMME NON CONNECTÉ, et c'est déjà le cas sans rien écrire de
 * plus : les deux se servent à la MÊME adresse — `/` change ses textes selon le
 * lecteur (voir `routes/Home.tsx`), pas son écran. Une règle qui aurait regardé la
 * session ici aurait retiré le pied de page à un visiteur qui découvre l'événement,
 * c'est-à-dire précisément à qui vient y chercher un lieu et un contact.
 *
 * ⚠️ ET UNE ADRESSE INCONNUE N'EN A PAS : `screenOf` ne reconnaît pas l'écran
 * « Adresse inconnue », donc la comparaison est fausse et rien ne s'affiche. C'est le
 * bon défaut — le pied de page se demande, il ne se subit pas.
 *
 * ⚠️ ÉCRITE ICI ET PAS DANS `Shell`, comme `isBare` juste au-dessus : le châssis n'a
 * pas de test qui le traverse, et « sur quels écrans » est une question de registre.
 * Posée dans le JSX, elle serait invérifiable.
 */
export function hasFooter(pathname: string): boolean {
  return screenOf(pathname)?.key === "home";
}

export const EVERY_SCREEN: ReadonlyArray<Screen> = [
  ...SCREENS,
  ...PAGE_SCREENS,
  ...LIVE_TOOLS,
];

/**
 * ═══ CE QUE LE SÉLECTEUR DE LA BARRE PROPOSE, dans ses groupes ═══
 *
 * Les écrans FIXES seulement : les pages du site et les étapes du parcours sont
 * tirées des réglages de l'événement, donc elles se composent dans la barre.
 *
 * ⚠️ ÉCRIT ICI POUR QU'UN TEST PUISSE LE VOIR. La liste vivait en JSX dans
 * `OrganiserBar`, et le test censé garantir qu'aucun écran n'est orphelin la
 * RECOPIAIT : il comparait `EVERY_SCREEN` à sa propre définition, donc il ne
 * pouvait pas échouer. Supprimer le groupe « Outils du direct » de la barre le
 * laissait vert pendant que la régie devenait introuvable — le défaut exact qu'il
 * était né pour empêcher.
 *
 * ⚠️ ET CE QU'IL NE VOIT TOUJOURS PAS, dit franchement : le projet n'a pas de rendu
 * de composants dans ses tests (ni jsdom ni testing-library). Un test peut donc
 * garantir que cette liste couvre tous les écrans ; il ne peut pas garantir que la
 * barre la parcourt. C'est pour ça que la barre n'a plus qu'UNE boucle, sur ces
 * groupes : la surface non couverte tient en trois lignes de JSX.
 */
export const PICKER_GROUPS: ReadonlyArray<{
  /** `null` = en tête de liste, sans intitulé de groupe. */
  label: string | null;
  screens: ReadonlyArray<Screen>;
}> = [
  { label: null, screens: SCREENS.filter((screen) => !screen.funnel) },
  { label: "Outils du direct", screens: LIVE_TOOLS },
];

/**
 * The switch that keeps a step in the funnel. On until somebody turns it off.
 *
 * ⚠️ PAR INSTANCE POUR UNE ÉTAPE RÉPÉTÉE, sinon retirer la deuxième étape
 * « informations » retirerait aussi la première : elles partageraient un interrupteur.
 * La PREMIÈRE garde la clé sans suffixe — c'est celle qui existe déjà en base.
 */
export function stepShowKey(screen: Screen, instance?: string): string {
  const base = `FO_MEETING_V2_STEP_${screen.key.toUpperCase()}_SHOW`;
  return instance && instance !== "1" ? `${base}_${instance}` : base;
}

/**
 * ⚠️ THE SITE'S PAGES INCLUDED, and three things depend on it at once: the panel's scope
 * (`CATALOG[key]`, hence the badge's settings), the name the bar displays, and the
 * session guard — a personal page does not open without a token, exactly like a step of
 * the funnel.
 */
/** L'étape qu'un événement peut servir plusieurs fois. */
export const REPEATABLE_STEP = SCREENS.find((s) => s.repeatable);

export function screenOf(pathname: string): Screen | undefined {
  const exact = EVERY_SCREEN.find((s) => s.path === pathname);
  if (exact) return exact;

  /**
   * ⚠️ UNE INSTANCE DE L'ÉTAPE RÉPÉTABLE : `/registration/informations/<id>`. Reconnue
   * par son PRÉFIXE, et non par la liste des instances — celle-ci vit dans la
   * configuration, qui arrive après le routeur et à laquelle cette fonction n'a pas
   * accès. Trois choses en dépendent et se règlent d'un coup : la portée du panneau, la
   * garde de session, et « suis-je dans le tunnel ? ».
   */
  if (REPEATABLE_STEP && pathname.startsWith(`${REPEATABLE_STEP.path}/`)) {
    return REPEATABLE_STEP;
  }

  /**
   * ⚠️ UN EXEMPLAIRE D'UNE PAGE MULTIPLIABLE : `/infos/<id>`, `/form/<id>`. Même règle
   * de préfixe, même raison, et mêmes dépendants : c'est ce qui donne à `/infos/2` sa
   * portée de panneau (`CATALOG.infos`), sa garde de session et son nom dans la barre.
   * UN SEUL segment de plus : `/infos/2/x` ne route vers rien, le prétendre ici ferait
   * mentir la barre sur une adresse introuvable.
   */
  for (const multipliable of REPEATABLE_PAGES) {
    if (!pathname.startsWith(`${pagePath(multipliable)}/`)) continue;
    const remaining = pathname.slice(pagePath(multipliable).length + 1);
    if (remaining && !remaining.includes("/")) {
      return PAGE_SCREENS.find((s) => s.key === multipliable.key);
    }
  }
  return undefined;
}

/**
 * A screen nobody knows is NOT open. An address that matches no entry falls to
 * the not-found screen, and treating the unknown as public would make a typo a
 * way around the guard.
 */
export function needsSession(pathname: string): boolean {
  return screenOf(pathname)?.open !== true;
}

/**
 * ═══ THE CONTEXT SEGMENT, ADDED AND REMOVED ═══
 *
 * Screens are declared at their canonical path (`/`, `/login`). The event
 * token is a PREFIX, and only these two functions know it. Without them every
 * screen, every link and every lookup would have to remember to add it — and the
 * one that forgets sends a participant out of their own event.
 *
 * Both pure, so the awkward cases are settled by tests: the bare prefix, a token
 * that happens to be a prefix of a longer path, no token at all.
 */
/**
 * ⚠️ AN ADDRESS IS PERCENT-ENCODED, A ROUTE PARAMETER IS NOT — and comparing the two
 * raw is what this function got wrong.
 *
 * `useLocation().pathname` carries what the address bar carries:
 * `/sportachat%C3%A9t%C3%A92025/…`. `useParams().context` carries the DECODED value,
 * `sportachatété2025`. The prefix therefore never matched on any event whose alias holds
 * a character outside ASCII — an accent is enough — and the path came back untouched.
 *
 * Everything downstream reads that result, so ONE mismatch took out the lot: the screen
 * was no longer recognised (`screenOf`), so the panel said "this screen has no settings
 * of its own", the organiser bar said "Hors écran", the funnel lost its breadcrumb and
 * its desktop layout, and every navigation guard went quiet. Seen on
 * `sportachatété2025`, in production data.
 */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    // A malformed sequence is not a reason to bring the site down: left as is, it
    // simply will not match the token, which is the safe outcome.
    return segment;
  }
}

export function stripContext(pathname: string, token?: string): string {
  if (!token) return pathname;
  /**
   * ⚠️ COMPARED SEGMENT BY SEGMENT, not by string prefix. It settles the encoding on
   * its own — only the first segment is decoded, and only for the comparison — and it
   * keeps the case a prefix test was written for: a token "sal" must not swallow the
   * first three letters of "/salon", and it does not, because "salon" is not "sal".
   */
  const [, first = "", ...rest] = pathname.split("/");
  if (decodeSegment(first) !== token) return pathname;
  // The bare prefix is the home screen; the rest of the path is made of the routes'
  // own ASCII segments, so it travels back untouched.
  return rest.length > 0 ? `/${rest.join("/")}` : "/";
}

export function withContext(path: string, token?: string): string {
  if (!token) return path;
  return path === "/" ? `/${token}` : `/${token}${path}`;
}
